import React from 'react';
import PropTypes from 'prop-types';
import SearchDropdown from './SearchDropdown';
import './PlaybookPicker.css';

/**
 * Trigger + popover for picking which playbook this chat talks to (or
 * "Auto", which picks one from the first message's content — see
 * PlaybooksService.pickBestPlaybook). Thin wrapper over SearchDropdown
 * (the generic version of this same trigger+search+icon+description
 * popover, extracted so UnifiedTopBar's service selector could reuse it
 * too — see SearchDropdown.jsx).
 *
 * Deliberately NOT part of the vendored chat widget's own "Playbook" info
 * button (which just describes whatever is currently active) — this is a
 * separate, mia-flow-fe-owned control that decides which playbook that
 * button ends up describing.
 */
export default function PlaybookPicker({ playbooks, selected, onSelect, disabled, disabledReason, resolvedName }) {
  const selectedPlaybook = playbooks.find((p) => p.name === selected);
  // While `selected === 'auto'`, show WHICH playbook Auto actually resolved
  // to (including a deterministic fallback default — see mia-flow-bff's
  // selectAiPlaybook) instead of a bare "Auto" that gives no visibility into
  // what's actually about to run, or already ran, this turn.
  const resolvedPlaybook = resolvedName ? playbooks.find((p) => p.name === resolvedName) : null;
  const triggerLabel = selected === 'auto'
    ? (resolvedPlaybook ? `Auto · ${resolvedPlaybook.displayName}` : 'Auto')
    : (selectedPlaybook?.displayName || selected);

  const items = playbooks.map((p) => ({
    value: p.name,
    icon: 'book',
    title: p.displayName,
    desc: p.description,
  }));

  return (
    <div className="playbook-picker">
      <SearchDropdown
        disabled={disabled}
        emptyText='No assistant matches "{query}"'
        footer="Applies to your next new chat in this project."
        items={items}
        onSelect={onSelect}
        pinnedItem={{ value: 'auto', icon: 'sparkles', title: 'Auto', desc: 'Pick the best assistant based on what you ask for' }}
        searchPlaceholder="Search assistants…"
        selectedValue={selected}
        triggerIcon={selected === 'auto' ? 'sparkles' : 'book'}
        triggerLabel={triggerLabel}
        // Was "Choose which playbook this chat uses" — "playbook" is internal
        // vocabulary a first-time user has no reason to know, and this
        // control sits right next to the chat widget's OWN "Auto" tool-
        // approval toggle (unrelated, vendored, not editable from this repo)
        // with the exact same visible "Auto" label — spelling out what each
        // one actually governs in its own tooltip is the only disambiguation
        // available without touching the vendored widget.
        triggerTitle={disabled ? disabledReason : 'Choose which assistant answers in this chat'}
      />
    </div>
  );
}

PlaybookPicker.propTypes = {
  playbooks: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    displayName: PropTypes.string,
    description: PropTypes.string,
  })).isRequired,
  selected: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  disabledReason: PropTypes.string,
  /** Only meaningful while `selected === 'auto'` — the playbook Auto actually
   *  resolved to (or is about to), shown alongside the "Auto" label. */
  resolvedName: PropTypes.string,
};
